import React, { useEffect, useMemo, useState } from 'react';
import { plannedSegments, plannedSchedule as planSchedule } from './plannedSegments';
import RRHHNativeShell from './RRHHNativeShell';
import { rrhhCoreGet, rrhhCorePost } from './rrhhCoreApi';
import { personMatchesName } from '../../utils/personnel';
import {
  OPERATION_BANDS,
  MARK_STATUSES,
  addIsoDays,
  canSubmitOperations,
  currentIsoDate,
  formatDbDateTime,
  formatDisplayDate,
  formatDisplayTime,
  minutesFromTime,
  normalizeStatus,
  normalizeSearchText,
  normalizeTime,
  operationBandsForTariff,
  operationTariffLabel,
  parseDbDateTime,
  personDisplayName,
  personOperationalRole,
} from './rrhhUtils';
import haptic from '../../utils/haptic';
import './RRHHNative.css';

const CLOCK_FIXED_REFERENCES = [
  'Depósito',
  'Mantenimiento',
  'Logística',
  'Administración',
  'Herrería',
  'Elías Regules',
];

function eventReferenceNames(rows = []) {
  const seen = new Set();
  return rows
    .map((row) => {
      const name = String(row?.name || '').trim();
      const place = String(row?.lugar || '').trim();
      return name ? (place ? `${name} - ${place}` : name) : '';
    })
    .filter((label) => {
      const key = label.toLocaleLowerCase('es');
      if (!label || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

// Todos son del día elegido en el formulario, así que la fecha no se repite en
// cada renglón; la hora sí, que es el orden en que llegan y ayuda a distinguir.
function operationEventReferenceOptions(rows = []) {
  const seen = new Set();
  return rows
    .map((row) => {
      const name = String(row?.name || '').trim();
      if (!name) return null;
      const hora = String(row?.hora || '').slice(0, 5);
      return { value: name, label: hora ? `${hora} · ${name}` : name };
    })
    .filter((option) => {
      const key = option?.value.toLocaleLowerCase('es');
      if (!option || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function ClockReferenceOptions({ events = [] }) {
  return (
    <>
      {CLOCK_FIXED_REFERENCES.map((reference) => <option key={reference} value={reference}>{reference}</option>)}
      {events.length > 0 && <option disabled value="__clock-reference-divider__">────────</option>}
      {events.map((eventName) => <option key={eventName} value={eventName}>{eventName}</option>)}
    </>
  );
}

// Valor centinela del desplegable de referencia: al elegirlo se pasa a texto
// libre. No puede colisionar con un nombre de evento real.
const OTRA_REFERENCIA = '__otra-referencia__';

function elapsedLabel(entryDate) {
  if (!entryDate) return 'Sin entrada activa';
  const totalSeconds = Math.max(0, Math.floor((Date.now() - entryDate.getTime()) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function groupDayTurns(turns = []) {
  const groups = new Map();
  turns.flatMap((turn) => {
    const segments = plannedSegments(turn);
    return segments.length ? segments.map((segment) => ({ ...turn, ...segment })) : [turn];
  }).forEach((turn) => {
    const status = normalizeStatus(turn.estado);
    const activity = String(turn.actividad_ubicacion || '').trim();
    if (!activity && MARK_STATUSES.includes(status)) return;
    if (status === 'VACIO' || status === 'LIBRE') return;
    const start = normalizeTime(turn.hora_inicio);
    const end = normalizeTime(turn.hora_fin);
    const task = activity || status || 'Turno';
    const schedule = start ? `${start} a ${end || '--:--'}` : 'Sin horario';
    const key = `${normalizeStatus(task)}|${start}|${end}`;
    if (!groups.has(key)) {
      groups.set(key, {
        task,
        schedule,
        startMinutes: minutesFromTime(start) ?? Number.MAX_SAFE_INTEGER,
        people: [],
      });
    }
    const personName = String(turn.persona || '').trim();
    if (personName && !groups.get(key).people.includes(personName)) {
      groups.get(key).people.push(personName);
    }
  });
  return [...groups.values()]
    .map((group) => ({
      ...group,
      people: group.people.sort((left, right) => left.localeCompare(right, 'es')),
    }))
    .sort((left, right) => {
      const byStart = left.startMinutes - right.startMinutes;
      if (byStart) return byStart;
      return left.task.localeCompare(right.task, 'es');
    });
}

function parsePublishedPlanDates(rows = []) {
  const row = rows.find((item) => item.clave === 'published_plan_dates');
  try {
    const parsed = Array.isArray(row?.valor) ? row.valor : JSON.parse(row?.valor || '[]');
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function currentLocation() {
  if (!navigator.geolocation) {
    return Promise.resolve({ label: 'Ubicación no disponible', matched: false, coords: null });
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        label: 'Ubicación registrada',
        matched: true,
        coords: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        },
      }),
      () => resolve({ label: 'Ubicación pendiente', matched: false, coords: null }),
      { enableHighAccuracy: true, timeout: 7000, maximumAge: 60000 }
    );
  });
}

export default function RelojPage() {
  const [clock, setClock] = useState(new Date());
  const [operator, setOperator] = useState(null);
  const [todayTurns, setTodayTurns] = useState([]);
  const [visibleDate, setVisibleDate] = useState(currentIsoDate());
  const [visibleTurns, setVisibleTurns] = useState([]);
  const [markEventReferences, setMarkEventReferences] = useState([]);
  const [operationEventReferences, setOperationEventReferences] = useState([]);
  const [publishedPlanDates, setPublishedPlanDates] = useState(new Set());
  const [marks, setMarks] = useState([]);
  const [openEntry, setOpenEntry] = useState(null);
  const [tariffs, setTariffs] = useState([]);
  const [project, setProject] = useState('');
  const [operationForm, setOperationForm] = useState({ date: currentIsoDate(), tariff: '', band: OPERATION_BANDS[0], project: '', note: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [toastKind, setToastKind] = useState('success');
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      rrhhCoreGet('/mi-persona'),
      rrhhCoreGet('/configuracion').catch(() => []),
      rrhhCoreGet('/operacion-tarifas?activas=1').catch(() => []),
    ])
      .then(([person, configRows, tariffRows]) => {
        if (!active) return;
        setOperator(person);
        setPublishedPlanDates(parsePublishedPlanDates(configRows));
        setTariffs(tariffRows);
        setOperationForm((prev) => ({ ...prev, tariff: String(tariffRows[0]?.id || '') }));
      })
      .catch((err) => setError(err.message || 'No se pudo cargar el reloj'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);
  const operatorName = personDisplayName(operator);
  // La entrada abierta la decide el backend (misma lógica que valida el marcado
  // y sin ventana de fechas). Calcularla acá con las marcas de ayer y hoy dejaba
  // trabada a quien tuviera una entrada sin cerrar de días anteriores.
  const activeEntry = useMemo(() => parseDbDateTime(openEntry?.fecha_hora), [openEntry]);
  const plannedToday = todayTurns.find((turn) => String(turn.persona_id) === String(operator?.id));
  const plannedLocation = plannedToday?.actividad_ubicacion || project || 'Sin ubicación';
  const plannedSchedule = planSchedule(plannedToday) || 'Sin horario';
  const plannedDetail = plannedToday?.actividad_ubicacion || plannedToday?.estado || 'Sin detalle del plan semanal';
  const markReferences = useMemo(() => eventReferenceNames(markEventReferences), [markEventReferences]);
  const operationReferences = useMemo(() => operationEventReferenceOptions(operationEventReferences), [operationEventReferences]);
  const [otraReferencia, setOtraReferencia] = useState(false);
  const visibleTurnGroups = useMemo(() => groupDayTurns(visibleTurns), [visibleTurns]);
  const canUseOperations = canSubmitOperations(operator) && tariffs.length > 0;
  const selectedTariff = useMemo(
    () => tariffs.find((tariff) => String(tariff.id) === String(operationForm.tariff)) || null,
    [operationForm.tariff, tariffs]
  );
  const operationBands = useMemo(() => operationBandsForTariff(selectedTariff), [selectedTariff]);
  const canViewPlanDate = (date) => String(date) <= currentIsoDate() || publishedPlanDates.has(date);
  const isCurrentPersonName = (personName) => {
    if (!operator || !personName) return false;
    if (personMatchesName(operator, personName)) return true;
    return normalizeSearchText(personName) === normalizeSearchText(operatorName);
  };

  useEffect(() => {
    if (!operationBands.length) return;
    if (!operationBands.some((band) => band.band === operationForm.band)) {
      setOperationForm((prev) => ({ ...prev, band: operationBands[0].band }));
    }
  }, [operationBands, operationForm.band]);

  useEffect(() => {
    if (!operator) return;
    let active = true;
    const today = currentIsoDate();
    const yesterday = addIsoDays(today, -1);
    setLoading(true);
    setError('');
    Promise.all([
      rrhhCoreGet(`/turnos?desde=${today}&hasta=${today}`),
      rrhhCoreGet(`/turnos?desde=${visibleDate}&hasta=${visibleDate}`),
      rrhhCoreGet(`/reloj/referencias-eventos?fecha=${today}`),
      rrhhCoreGet(`/marcas?persona_id=${operator.id}&desde=${yesterday}&hasta=${today}`),
      rrhhCoreGet('/marcas/activa'),
    ])
      .then(([todayRows, visibleRows, eventRows, markRows, activePayload]) => {
        if (!active) return;
        setTodayTurns(todayRows);
        setVisibleTurns(visibleRows);
        setMarkEventReferences(eventRows);
        setMarks(markRows);
        setOpenEntry(activePayload?.entrada || null);
        setProject((current) => current || CLOCK_FIXED_REFERENCES[0]);
      })
      .catch((err) => setError(err.message || 'No se pudo cargar el reloj'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [operator, operatorName, visibleDate]);

  // La referencia de una operación ofrece los eventos DEL DÍA QUE SE ESTÁ
  // CARGANDO, no una ventana alrededor de hoy: la fecha ya se elige en el
  // formulario, así que la lista la sigue.
  useEffect(() => {
    if (!operator || !/^\d{4}-\d{2}-\d{2}$/.test(operationForm.date || '')) return;
    let active = true;
    rrhhCoreGet(`/reloj/referencias-eventos?fecha=${operationForm.date}`)
      .then((rows) => active && setOperationEventReferences(rows))
      .catch(() => active && setOperationEventReferences([]));
    return () => {
      active = false;
    };
  }, [operator, operationForm.date]);

  // kind 'error' pinta el cartel de rojo: antes todo salía en verde y un fallo
  // duro (p. ej. el rechazo del backend al marcar) se leía como confirmación.
  function showToast(message, kind = 'success') {
    setToast(message);
    setToastKind(kind);
    window.setTimeout(() => setToast(''), 2400);
  }

  async function refresh() {
    if (!operator) return;
    const today = currentIsoDate();
    const yesterday = addIsoDays(today, -1);
    const [todayRows, visibleRows, eventRows, markRows, activePayload] = await Promise.all([
      rrhhCoreGet(`/turnos?desde=${today}&hasta=${today}`),
      rrhhCoreGet(`/turnos?desde=${visibleDate}&hasta=${visibleDate}`),
      rrhhCoreGet(`/reloj/referencias-eventos?fecha=${today}`),
      rrhhCoreGet(`/marcas?persona_id=${operator.id}&desde=${yesterday}&hasta=${today}`),
      rrhhCoreGet('/marcas/activa'),
    ]);
    setTodayTurns(todayRows);
    setVisibleTurns(visibleRows);
    setMarkEventReferences(eventRows);
    setMarks(markRows);
    setOpenEntry(activePayload?.entrada || null);
  }

  async function registerMark(type) {
    if (normalizeStatus(type) === 'ENTRADA' && activeEntry) {
      showToast('Ya tenés una entrada activa. Marcá salida.', 'error');
      return;
    }
    if (normalizeStatus(type) === 'SALIDA' && !activeEntry) {
      showToast('Primero marcá entrada.', 'error');
      return;
    }
    setSaving(true);
    try {
      const location = await currentLocation();
      await rrhhCorePost('/marcas', {
        persona_id: operator.id,
        persona: operatorName,
        rol_operativo: personOperationalRole(operator),
        fecha_hora: formatDbDateTime(),
        tipo: type,
        tipo_marca: 'Por usuario',
        actividad_ubicacion: project,
        ubicacion_detectada: location.label,
        latitud: location.coords?.latitude ?? null,
        longitud: location.coords?.longitude ?? null,
        genera_incidencia: location.matched === false,
      });
    } catch (err) {
      // Doble pulso: la marca NO quedó. Es lo que no se ve si la persona ya
      // se dio vuelta después de tocar.
      haptic.warn();
      showToast(err.message || 'No se pudo registrar la marca', 'error');
      setSaving(false);
      return;
    }
    // Confirmación física de que el fichaje quedó registrado. Hasta ahora la
    // única señal era el cartel en pantalla.
    haptic.tick();
    // La marca YA quedó registrada. Un fallo de la recarga posterior no puede
    // reportarse como fallo del fichaje: antes lo hacía, la persona reintentaba
    // y el segundo intento se rechazaba por "ya hay una entrada activa".
    showToast(`${type} registrada`);
    try {
      await refresh();
    } catch {
      setError('La marca quedó registrada, pero no se pudo actualizar la pantalla. Recargá para verla.');
    } finally {
      setSaving(false);
    }
  }

  async function submitOperation(event) {
    event.preventDefault();
    if (!canUseOperations) {
      showToast('No tenés operaciones habilitadas', 'error');
      return;
    }
    if (!operationForm.project) {
      showToast('Elegí un proyecto', 'error');
      return;
    }
    setSaving(true);
    try {
      await rrhhCorePost('/operaciones', {
        operacion_tarifa_id: Number(operationForm.tariff),
        fecha_hora: `${operationForm.date || currentIsoDate()} 00:00:00`,
        franja: operationForm.band,
        referencia: operationForm.project,
        observacion: operationForm.note.trim(),
      });
      setOperationForm((prev) => ({ ...prev, project: '', note: '' }));
    } catch (err) {
      showToast(err.message || 'No se pudo enviar la operación', 'error');
      setSaving(false);
      return;
    }
    // Igual que en el marcado: la operación ya se guardó, un fallo de la
    // recarga no puede reportarse como si el envío hubiera fallado.
    showToast('Operación enviada');
    try {
      await refresh();
    } catch {
      setError('La operación quedó enviada, pero no se pudo actualizar la pantalla. Recargá para verla.');
    } finally {
      setSaving(false);
    }
  }

  async function moveVisibleDate(amount) {
    const nextDate = addIsoDays(visibleDate, amount);
    if (!canViewPlanDate(nextDate)) {
      showToast('Ese día todavía no está publicado', 'error');
      return;
    }
    setVisibleDate(nextDate);
  }

  function renderOperationPanel(className = '') {
    if (!canUseOperations) return null;
    // El valor elegido puede no estar en la lista del día (cambió la fecha, o
    // se escribió libre): en ese caso el select muestra "Otra referencia".
    const proyectoEnLista = operationReferences.some((option) => option.value === operationForm.project);
    const modoLibre = otraReferencia || (operationForm.project && !proyectoEnLista);
    return (
      <aside className={`rrhh-native-card rrhh-clock-operation-panel ${className}`.trim()}>
        <h2>Operación</h2>
        <form className="rrhh-native-form" onSubmit={submitOperation} style={{ marginTop: 14 }}>
          <label>
            Fecha
            <input type="date" value={operationForm.date} onChange={(event) => setOperationForm((prev) => ({ ...prev, date: event.target.value }))} />
          </label>
          <label>
            Categoría
            <select value={operationForm.tariff} onChange={(event) => setOperationForm((prev) => ({ ...prev, tariff: event.target.value }))}>
              {tariffs.map((tariff) => <option key={tariff.id} value={tariff.id}>{operationTariffLabel(tariff)}</option>)}
            </select>
          </label>
          <label>
            Franja
            <select value={operationForm.band} onChange={(event) => setOperationForm((prev) => ({ ...prev, band: event.target.value }))}>
              {(operationBands.length ? operationBands : OPERATION_BANDS.map((band) => ({ band }))).map((band) => <option key={band.band} value={band.band}>{band.band}</option>)}
            </select>
          </label>
          <label>
            Referencia
            {/* Un select de verdad: el datalist anterior se abría en la barra
                de sugerencias del teclado del celular, no como menú. */}
            <select
              value={modoLibre ? OTRA_REFERENCIA : operationForm.project}
              onChange={(event) => {
                const value = event.target.value;
                if (value === OTRA_REFERENCIA) {
                  setOtraReferencia(true);
                  setOperationForm((prev) => ({ ...prev, project: '' }));
                } else {
                  setOtraReferencia(false);
                  setOperationForm((prev) => ({ ...prev, project: value }));
                }
              }}
            >
              <option value="">{operationReferences.length ? 'Elegí un evento del día' : 'Sin eventos ese día'}</option>
              {operationReferences.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
              <option value={OTRA_REFERENCIA}>Otra referencia…</option>
            </select>
            {modoLibre && (
              <input
                value={operationForm.project}
                onChange={(event) => setOperationForm((prev) => ({ ...prev, project: event.target.value }))}
                placeholder="Escribí la referencia"
                autoFocus={otraReferencia}
              />
            )}
          </label>
          <label>
            Observación
            <textarea rows="3" value={operationForm.note} onChange={(event) => setOperationForm((prev) => ({ ...prev, note: event.target.value }))} placeholder="Detalle breve" />
          </label>
          <button className="btn btn-primary" type="submit" disabled={saving || !operationForm.tariff}>Enviar operación</button>
        </form>
      </aside>
    );
  }

  return (
    <RRHHNativeShell title="Reloj" subtitle={`${operatorName || 'Persona'} · ${formatDisplayDate(currentIsoDate())}`}>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="rrhh-clock-layout">
        <section className="rrhh-clock-hero">
          {/* La hora actual es lo único que baja de rango: el teléfono ya la
              muestra dos centímetros más arriba. El plan del día sí necesita
              peso propio —es contra qué se compara la jornada—, así que va
              aparte y no diluido en la misma línea. */}
          <p className="rrhh-clock-context">
            <span>{formatDisplayTime(clock)}</span>
          </p>

          <div className="rrhh-clock-plan">
            <small>Plan de hoy</small>
            <p>
              <strong>{plannedLocation}</strong>
              <span>{plannedSchedule}</span>
            </p>
          </div>

          <article className={`rrhh-native-card rrhh-clock-active${activeEntry ? ' on' : ''}`}>
            <small>Jornada activa</small>
            <strong>{activeEntry ? elapsedLabel(activeEntry) : 'Sin entrada'}</strong>
            <span>{activeEntry ? 'Entrada registrada' : plannedDetail}</span>
          </article>

          <article className="rrhh-native-card rrhh-native-form">
            <label className="rrhh-clock-ref">
              Referencia del día
              <select value={project} onChange={(event) => setProject(event.target.value)}>
                <ClockReferenceOptions events={markReferences} />
              </select>
              <small>Con qué queda registrada esta marca</small>
            </label>
            <div className="rrhh-clock-actions">
              <button className="rrhh-clock-button in" type="button" disabled={saving || Boolean(activeEntry)} onClick={() => registerMark('Entrada')}>Marcar entrada</button>
              <button className="rrhh-clock-button out" type="button" disabled={saving || !activeEntry} onClick={() => registerMark('Salida')}>Marcar salida</button>
            </div>
          </article>

          {renderOperationPanel('rrhh-clock-operation-mobile')}

          <article className="rrhh-native-card">
            <div className="rrhh-native-toolbar">
              <h2>Resumen del día</h2>
              <div className="rrhh-native-toolbar-group">
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => moveVisibleDate(-1)}>Anterior</button>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setVisibleDate(currentIsoDate())}>Hoy</button>
                <button className="btn btn-ghost btn-sm" type="button" disabled={!canViewPlanDate(addIsoDays(visibleDate, 1))} onClick={() => moveVisibleDate(1)}>Siguiente</button>
              </div>
            </div>
            <p>{formatDisplayDate(visibleDate)}</p>
            <div className="rrhh-native-list" style={{ marginTop: 12 }}>
              {!visibleTurnGroups.length && <div className="text-sm text-dim">Sin turnos operativos planificados para este día.</div>}
              {visibleTurnGroups.map((group) => {
                const isOwnGroup = group.people.some(isCurrentPersonName);
                return (
                  <div className={`rrhh-native-turn-row rrhh-clock-summary-row${isOwnGroup ? ' is-current-person-task' : ''}`} key={`${group.task}-${group.schedule}`}>
                    <div>
                      <strong className="rrhh-clock-summary-title">{group.schedule} - {group.task}</strong>
                    </div>
                    <div className="rrhh-clock-summary-people">
                      {group.people.map((personName) => (
                        <span className={isCurrentPersonName(personName) ? 'is-current-person' : ''} key={personName}>{personName}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </article>
        </section>

        {renderOperationPanel('rrhh-clock-operation-desktop')}
      </div>

      <div
        className={`rrhh-native-toast alert alert-${toastKind}${toast ? ' visible' : ''}`}
        role={toastKind === 'error' ? 'alert' : 'status'}
      >{toast}</div>
    </RRHHNativeShell>
  );
}
